import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

const principles = [
  {
    icon: "ri-flashlight-line",
    title: "更新及时",
    desc: "连载动漫按官方节奏第一时间同步，今日更新实时呈现，追更绝不落空。",
  },
  {
    icon: "ri-grid-fill",
    title: "分类清晰",
    desc: "热血、奇幻、恋爱、悬疑等分类一目了然，快速定位你想追的作品。",
  },
  {
    icon: "ri-calendar-check-line",
    title: "追番省心",
    desc: "追番日历按星期整理，一键定位每周更新，错过更新成为历史。",
  },
  {
    icon: "ri-eye-line",
    title: "免费观看",
    desc: "所有连载、新番与完结内容均免费在线观看，无需任何费用。",
  },
];

const reviews = [
  {
    name: "追番小王子",
    role: "资深二次元",
    text: "追番日历真的太好用了，再也不用靠备忘录记更新时间，打开日历就知道今天有哪些更新。",
  },
  {
    name: "橘子汽水",
    role: "日常番爱好者",
    text: "分类很清晰，我平时只追恋爱和日常番，筛选一下就能精准定位，体验很顺滑。",
  },
  {
    name: "夜班机长",
    role: "深夜追更党",
    text: "更新很及时，最新一话基本同步就上，画质流畅不卡顿，追更体验直接拉满。",
  },
];

const milestones = [
  { year: "2022", text: "妖精动漫网正式上线，专注新番更新追踪。", icon: "ri-rocket-line" },
  { year: "2023", text: "上线追番日历功能，按星期整理每周更新。", icon: "ri-calendar-2-line" },
  { year: "2024", text: "完善分类筛选与搜索，覆盖热血、恋爱、悬疑等题材。", icon: "ri-search-line" },
  { year: "2026", text: "累计收录作品超百部，成为追番用户喜爱的更新阵地。", icon: "ri-fire-line" },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于妖精动漫网",
  url: "https://comicnot.github.io/about",
  inLanguage: "zh-CN",
  mainEntity: {
    "@type": "Organization",
    name: "妖精动漫网",
    url: "https://comicnot.github.io/",
    description: "妖精动漫网专注新番更新追踪与追番日历，提供免费、及时的连载动漫在线观看体验。",
    email: "support@yaojing-anime.com",
  },
};

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const hp = (formData.get("website_alt") as string)?.trim() ?? "";
    formData.delete("website_alt");

    if (hp) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/da21g4bk8r5te5mqsdn0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <>
      <Header />
      <main className="bg-background-50">
        <section className="bg-background-100 py-8 md:py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-foreground-600">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li>/</li>
                <li aria-current="page" className="text-foreground-500">关于我们</li>
              </ol>
            </nav>
            <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">关于妖精动漫网</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              陪你追更每一天，让每一部心动的作品都不再错过。
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="text-xs font-medium text-primary-600">品牌介绍</span>
                <h2 className="mt-1 text-xl font-bold text-foreground-950 md:text-2xl">
                  专注新番更新追踪的追番阵地
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  妖精动漫网是专注于新番更新追踪与连载动漫推荐的追番网站。我们从追番用户的实际需求出发，把每周更新的作品按星期整理成清晰的追番日历，让你打开网站就能快速定位正在更新的作品，点开即看最新话。
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  自上线以来，妖精动漫网不断优化分类筛选、搜索与更新同步体验，累计收录热血、奇幻、恋爱、悬疑、搞笑、日常、科幻等多题材连载作品，成为众多追番爱好者信赖的更新阵地。
                </p>
              </div>
              <div className="aspect-[16/10] w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=cozy%20anime%20illustration%20of%20a%20team%20working%20together%20around%20a%20table%20with%20screens%20showing%20anime%20schedule%2C%20warm%20golden%20room%20lighting%2C%20friendly%20collaborative%20atmosphere%2C%20high%20quality%20digital%20anime%20art%2C%20no%20text&width=800&height=500&seq=about-studio&orientation=landscape"
                  alt="妖精动漫网团队工作场景插画"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6">
              <span className="text-xs font-medium text-accent-600">内容原则</span>
              <h2 className="mt-1 text-xl font-bold text-foreground-950 md:text-2xl">
                我们坚持的四件事
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p) => (
                <div
                  key={p.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6">
              <span className="text-xs font-medium text-secondary-700">发展理念</span>
              <h2 className="mt-1 text-xl font-bold text-foreground-950 md:text-2xl">
                一步步陪你追更
              </h2>
            </div>
            <div className="relative space-y-6 border-l border-background-200 pl-8">
              {milestones.map((m) => (
                <div key={m.year} className="relative">
                  <span className="absolute -left-[45px] flex h-7 w-7 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className={`${m.icon} text-sm`}></i>
                  </span>
                  <p className="font-heading text-lg text-secondary-700">{m.year}</p>
                  <p className="mt-1 text-sm text-foreground-700">{m.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6">
              <span className="text-xs font-medium text-primary-600">用户口碑</span>
              <h2 className="mt-1 text-xl font-bold text-foreground-950 md:text-2xl">
                追番用户怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {reviews.map((r) => (
                <figure key={r.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex gap-0.5 text-secondary-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">
                    “{r.text}”
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                      <i className="ri-user-smile-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{r.name}</p>
                      <p className="text-xs text-foreground-500">{r.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div>
                <span className="text-xs font-medium text-accent-600">联系方式</span>
                <h2 className="mt-1 text-xl font-bold text-foreground-950 md:text-2xl">
                  随时联系我们
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  无论是追番建议、内容反馈还是合作洽谈，都欢迎通过以下方式联系我们，我们会在收到后尽快回复。
                </p>
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">联系邮箱</p>
                      <p className="text-sm font-bold text-foreground-900">support@yaojing-anime.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-time-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">客服时间</p>
                      <p className="text-sm font-bold text-foreground-900">每天 9:00 - 22:00</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-global-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">在线追番</p>
                      <p className="text-sm font-bold text-foreground-900">7×24 小时更新同步</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="text-base font-bold text-foreground-950">给我们留言</h3>
                <p className="mt-1 text-sm text-foreground-600">填写下方表单，我们会尽快与你联系。</p>

                {status === "success" && (
                  <div className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm font-medium text-accent-800">
                    <i className="ri-checkbox-circle-line"></i>
                    提交成功，感谢你的留言，我们会尽快与你联系！
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-medium text-primary-800">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </div>
                )}

                <form
                  id="contact-form"
                  data-readdy-form="妖精动漫网联系我们咨询表单"
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div className="frm-hp-field" aria-hidden="true">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex-1">
                      <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        您的称呼
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        maxLength={50}
                        placeholder="怎么称呼您"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        联系邮箱
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      留言主题
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      maxLength={80}
                      placeholder="如：追番建议 / 内容反馈 / 合作洽谈"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="想对我们说什么都可以，最多 500 字"
                      className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                  >
                    {status === "submitting" ? "提交中..." : "提交留言"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </>
  );
}